/**
 * MessageContext — conversations & message store for LAUNCHER-2.0.
 *
 * Owns the conversation list, per-conversation message arrays, and
 * the sendMessage() transport that pushes queries through the WebSocket.
 *
 * Registers WS event handlers for: DELEGATE, THINK, ACT, OBSERVE, ANSWER, ERROR.
 *
 * Exposes:
 *   conversations, setConversations
 *   activeConversation, setActiveConversation
 *   messages              — derived from activeConversation
 *   sendMessage(content)
 *   createConversation(title)
 *   setPendingMessage
 *   addMessage(convId, msg)          — also used by ClarificationContext
 *   updateMessages(convId, updater)  — also used by ClarificationContext
 *   activeConversationRef            — ref for async handlers
 *   lastUserQueryRef                 — ref for ClarificationContext
 *   deleteConversation(id)
 */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { EventType } from '../services/orchestratorClient';
import { useAuth } from './AuthContext';
import { useConnection } from './ConnectionContext';
import { extractDeveloperTrace } from '../contracts/clarification';
import { initialAgents, initialConversations } from '../data/seedAgents';
import { sanitizeInput } from '../services/sanitize';
import { createLogger } from '../utils/logger';
import { apiFetch } from '../services/apiFetch';

const log = createLogger('MessageContext');

const MessageContext = createContext(null);

// ── Shared ID generator ───────────────────────────────────────────────────────
let _msgSeq = 0;
export function generateMsgId() {
  return `msg-${Date.now()}-${_msgSeq++}`;
}

// ── Relative time helper ──────────────────────────────────────────────────────
function _relativeTime(isoStr) {
  if (!isoStr) return 'Just now';
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function MessageProvider({ children }) {
  const { user } = useAuth();
  const { client, ensureConnected, setConnectionStatus, perf } = useConnection();

  const [conversations, setConversations] = useState(initialConversations);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messagesMap, setMessagesMap] = useState({});
  const [pendingMessage, setPendingMessage] = useState(null);

  const messages = messagesMap[activeConversation] || [];

  const activeConversationRef = useRef(activeConversation);
  const lastUserQueryRef = useRef('');

  useEffect(() => { activeConversationRef.current = activeConversation; }, [activeConversation]);

  // ── Load persisted conversations from DB when user is available ───────────
  useEffect(() => {
    if (!user) return;
    apiFetch('/conversations')
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        if (Array.isArray(list) && list.length > 0) {
          setConversations(list.map(c => ({
            id: c.id,
            title: c.title,
            preview: c.preview || '',
            timestamp: _relativeTime(c.updated_at),
          })));
        }
      })
      .catch(() => {}); // silent fail — local state is the fallback
  }, [user]);

  // ── Batched messagesMap updater ────────────────────────────────────────────
  // Rapid WS events (DELEGATE, THINK, ACT, OBSERVE) queue functional updates
  // and flush them in a single setState via requestAnimationFrame.
  const pendingUpdates = useRef([]);
  const rafId = useRef(null);

  const flushUpdates = useCallback(() => {
    rafId.current = null;
    const updates = pendingUpdates.current;
    if (updates.length === 0) return;
    pendingUpdates.current = [];
    setMessagesMap(prev => updates.reduce((state, fn) => fn(state), prev));
  }, []);

  const batchMessagesUpdate = useCallback((updater) => {
    pendingUpdates.current.push(updater);
    if (rafId.current == null) {
      rafId.current = requestAnimationFrame(flushUpdates);
    }
  }, [flushUpdates]);

  // Cleanup RAF on unmount
  useEffect(() => () => { if (rafId.current != null) cancelAnimationFrame(rafId.current); }, []);

  // ── Message helpers ─────────────────────────────────────────────────────────
  const addMessage = useCallback((convId, msg) => {
    setMessagesMap(prev => ({
      ...prev,
      [convId]: [...(prev[convId] || []), msg],
    }));
  }, []);

  const updateLastOrchestratorMessage = useCallback((convId, updater) => {
    batchMessagesUpdate(prev => {
      const msgs = prev[convId] || [];
      const lastOrchIdx = msgs.map((m, i) => m.role === 'orchestrator' ? i : -1).filter(i => i >= 0).pop();
      if (lastOrchIdx === undefined) return prev;
      const updated = msgs.map((m, i) => i === lastOrchIdx ? updater(m) : m);
      return { ...prev, [convId]: updated };
    });
  }, [batchMessagesUpdate]);

  const updateMessages = useCallback((convId, updater) => {
    setMessagesMap(prev => ({
      ...prev,
      [convId]: updater(prev[convId] || []),
    }));
  }, []);

  // ── Conversation management ─────────────────────────────────────────────────
  const createConversation = useCallback(async (title) => {
    const convTitle = title || 'New Conversation';
    // 1. Optimistically create with a temp ID so callers (App.jsx) can
    //    proceed synchronously — sendMessage reads activeConversationRef
    //    which must be set before any message is dispatched.
    const tempId = 'conv-' + Date.now();
    setConversations(prev => [{ id: tempId, title: convTitle, timestamp: 'Just now', preview: '' }, ...prev]);
    setActiveConversation(tempId);
    activeConversationRef.current = tempId;
    setMessagesMap(prev => ({ ...prev, [tempId]: [] }));

    // 2. Upgrade to a real server-assigned UUID in the background.
    try {
      const res = await apiFetch('/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: convTitle, preview: '' }),
      });
      if (res.ok) {
        const data = await res.json();
        const realId = data?.id;
        if (realId) {
          setConversations(prev => prev.map(c => c.id === tempId ? { ...c, id: realId } : c));
          setActiveConversation(realId);
          activeConversationRef.current = realId;
          setMessagesMap(prev => {
            const { [tempId]: msgs, ...rest } = prev;
            return { ...rest, [realId]: msgs || [] };
          });
          return realId;
        }
      }
    } catch {
      // Keep tempId — conversation lives in memory only (no DB persistence)
    }
    return tempId;
  }, []);

  // ── Lazy-load messages on conversation switch ───────────────────────────────
  const switchConversation = useCallback((id) => {
    setActiveConversation(id);
    // Only fetch from DB if we have no local messages for this conversation
    // and the ID looks like a UUID (not a temp conv-{timestamp} ID)
    if (!messagesMap[id] && id && !id.startsWith('conv-')) {
      apiFetch(`/conversations/${id}/messages`)
        .then(r => r.ok ? r.json() : [])
        .then(msgs => {
          if (!Array.isArray(msgs)) return;
          const uiMsgs = msgs.map(m => ({
            id: 'db-' + m.id,
            role: m.role,
            content: m.content,
            timestamp: new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            ...(m.role === 'agent' ? {
              agentId: m.agent_id || 'unknown',
              agentName: m.agent_name || 'Agent',
              agentIcon: '🤖',
            } : {}),
          }));
          setMessagesMap(prev => ({ ...prev, [id]: uiMsgs }));
        })
        .catch(() => {});
    }
  }, [messagesMap]);

  // ── Delete conversation ─────────────────────────────────────────────────────
  const deleteConversation = useCallback((id) => {
    setConversations(prev => prev.filter(c => c.id !== id));
    setMessagesMap(prev => { const n = { ...prev }; delete n[id]; return n; });
    if (activeConversation === id) setActiveConversation(null);
    // Best-effort API delete — only for persisted (UUID) conversations
    if (id && !id.startsWith('conv-')) {
      apiFetch('/conversations/' + id, { method: 'DELETE' }).catch(() => {});
    }
  }, [activeConversation]);

  // ── WebSocket event handlers ────────────────────────────────────────────────
  // Track which query_ids have already had their first-response latency recorded
  const respondedQueries = useRef(new Set());

  const recordFirstResponse = useCallback((queryId) => {
    if (queryId && !respondedQueries.current.has(queryId)) {
      respondedQueries.current.add(queryId);
      perf.markQueryResponse(queryId);
    }
  }, [perf]);

  useEffect(() => {
    const handleDelegate = (event) => {
      recordFirstResponse(event.query_id);
      const convId = activeConversationRef.current;
      updateLastOrchestratorMessage(convId, (m) => {
        if (!m.isStreaming) return m;
        const delegations = [...(m.delegations || []), {
          agent: event.to_agent_name,
          task: event.reason || 'Processing request',
          status: 'in-progress',
        }];
        return { ...m, delegations, content: `Routing to **${event.to_agent_name}**...` };
      });
    };

    const handleThink = (event) => {
      const convId = activeConversationRef.current;
      updateLastOrchestratorMessage(convId, (m) =>
        m.isStreaming ? { ...m, content: event.reasoning || 'Thinking...' } : m
      );
    };

    const handleAct = (event) => {
      const convId = activeConversationRef.current;
      const agentObj = initialAgents.find(a => a.id === event.agent_id);
      const agentName = agentObj?.name || event.agent_id;
      updateLastOrchestratorMessage(convId, (m) => {
        if (!m.isStreaming) return m;
        const delegations = [...(m.delegations || [])];
        if (!delegations.find(d => d.toolKey === event.tool_name)) {
          delegations.push({
            toolKey: event.tool_name,
            agent: agentName,
            task: `Calling ${event.tool_name}`,
            status: 'in-progress',
          });
        }
        return { ...m, delegations };
      });
    };

    const handleObserve = (event) => {
      const convId = activeConversationRef.current;
      updateLastOrchestratorMessage(convId, (m) => {
        if (!m.isStreaming) return m;
        const delegations = (m.delegations || []).map(d =>
          d.toolKey === event.tool_name
            ? {
                ...d,
                status: event.success ? 'completed' : 'error',
                task: `${event.tool_name} · ${event.duration_ms}ms`,
              }
            : d
        );
        return { ...m, delegations };
      });
    };

    const handleAnswer = (event) => {
      recordFirstResponse(event.query_id);
      const convId = activeConversationRef.current;
      const userQuery = lastUserQueryRef.current;
      const agent = initialAgents.find(a => a.id === event.agent_id) || { name: 'Assistant', icon: '🤖' };

      log.info('answer_received', {
        queryId: event.query_id,
        agentId: event.agent_id,
        requestId: event.request_id,
      });

      updateMessages(convId, (msgs) => {
        const updated = msgs.map(m =>
          m.role === 'orchestrator' && m.isStreaming ? { ...m, isStreaming: false } : m
        );
        return [...updated, {
          id: generateMsgId(),
          role: 'agent',
          content: event.answer,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          agentId: event.agent_id || 'assistant',
          agentName: agent.name,
          agentIcon: agent.icon,
          confidence: event.confidence,
          grounded: event.grounded,
          sources: event.sources,
          trace: extractDeveloperTrace(event),
          requestId: event.request_id || null,
        }];
      });

      // Persist user message + agent response to DB (fire-and-forget)
      if (convId && !convId.startsWith('conv-')) {
        const agentContent = event.answer || event.content || '';
        apiFetch(`/conversations/${convId}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: [
              { role: 'user', content: userQuery },
              { role: 'agent', content: agentContent, agent_id: event.agent_id, agent_name: agent.name },
            ],
          }),
        }).catch(() => {}); // fire-and-forget
        // Bump conversation updated_at and preview
        apiFetch(`/conversations/${convId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ preview: (agentContent || '').slice(0, 120) }),
        }).catch(() => {});
      }
    };

    const handleError = (event) => {
      recordFirstResponse(event.query_id);
      const convId = activeConversationRef.current;
      const errorCode = event.error || 'UNKNOWN_ERROR';
      const errorMessage = event.message || 'An unexpected error occurred. Please try again.';
      const requestId = event.request_id;

      log.error('query_error', {
        queryId: event.query_id,
        requestId,
        errorCode,
        errorMessage,
      });

      const displayParts = [`**Error:** ${errorMessage}`];
      if (requestId) {
        displayParts.push(`\n\n_Request ID: \`${requestId}\`_`);
      }

      updateMessages(convId, (msgs) => {
        const updated = msgs.map(m =>
          m.role === 'orchestrator' && m.isStreaming ? { ...m, isStreaming: false } : m
        );
        return [...updated, {
          id: generateMsgId(),
          role: 'system',
          content: displayParts.join(''),
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isError: true,
          errorCode,
          requestId,
        }];
      });
      setConnectionStatus('error');
    };

    client.on(EventType.DELEGATE, handleDelegate);
    client.on(EventType.THINK, handleThink);
    client.on(EventType.ACT, handleAct);
    client.on(EventType.OBSERVE, handleObserve);
    client.on(EventType.ANSWER, handleAnswer);
    client.on(EventType.ERROR, handleError);

    return () => {
      client.off(EventType.DELEGATE, handleDelegate);
      client.off(EventType.THINK, handleThink);
      client.off(EventType.ACT, handleAct);
      client.off(EventType.OBSERVE, handleObserve);
      client.off(EventType.ANSWER, handleAnswer);
      client.off(EventType.ERROR, handleError);
    };
  }, [client, recordFirstResponse, updateLastOrchestratorMessage, updateMessages, setConnectionStatus]);

  // ── Send message ────────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (content) => {
    const sanitizedContent = sanitizeInput(content);
    const convId = activeConversationRef.current;
    const ts = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    lastUserQueryRef.current = sanitizedContent;
    const userMsg = { id: generateMsgId(), role: 'user', content: sanitizedContent, timestamp: ts };
    addMessage(convId, userMsg);

    setConversations(prev => prev.map(c =>
      c.id === convId && (c.title === 'New Conversation' || c.preview === '')
        ? { ...c, title: sanitizedContent.length > 40 ? sanitizedContent.substring(0, 40) + '…' : sanitizedContent, preview: sanitizedContent.substring(0, 60) }
        : c
    ));

    try {
      await ensureConnected();

      const thinkingMsg = {
        id: generateMsgId(),
        role: 'orchestrator',
        content: 'Analyzing your request and routing to the appropriate agents...',
        timestamp: ts,
        agentId: 'orchestrator',
        delegations: [],
        isStreaming: true,
      };
      addMessage(convId, thinkingMsg);

      const queryId = client.sendQuery(sanitizedContent, null, user?.oid || '', convId);
      if (queryId) perf.markQuerySent(queryId);
    } catch (error) {
      log.error('send_failed', { error: error?.message });
      setConnectionStatus('error');
      addMessage(convId, {
        id: generateMsgId(),
        role: 'system',
        content: '**Connection error:** Unable to reach the orchestrator. Please check your connection and try again.',
        timestamp: ts,
        isError: true,
      });
    }
  }, [client, user, ensureConnected, setConnectionStatus, addMessage, perf]);

  // ── Handle pending messages (set after conversation switch) ─────────────────
  useEffect(() => {
    if (!pendingMessage) return;
    const msg = pendingMessage;
    setPendingMessage(null);
    sendMessage(msg);
  }, [pendingMessage, sendMessage]);

  const contextValue = useMemo(() => ({
    conversations,
    setConversations,
    activeConversation,
    setActiveConversation: switchConversation,
    messages,
    sendMessage,
    createConversation,
    setPendingMessage,
    addMessage,
    updateMessages,
    activeConversationRef,
    lastUserQueryRef,
    deleteConversation,
    switchConversation,
  }), [conversations, activeConversation, messages, sendMessage, createConversation, addMessage, updateMessages, deleteConversation, switchConversation]);

  return (
    <MessageContext.Provider value={contextValue}>
      {children}
    </MessageContext.Provider>
  );
}

export function useMessages() {
  const ctx = useContext(MessageContext);
  if (!ctx) throw new Error('useMessages must be used within MessageProvider');
  return ctx;
}
